import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Link } from "wouter";
import { useLanguage } from "@/contexts/language";
import { useSubscription } from "@/contexts/subscription";
import {
  Flame, Trophy, CalendarDays, Target, Crosshair, TrendingUp, Lock,
  CheckCircle2, BookOpen, Clock, BarChart3, Zap, Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchWithAuth } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";

async function fetchAnalytics() {
  return fetchWithAuth<AnalyticsData>("/api/stats/analytics");
}

interface AnalyticsData {
  currentStreak: number;
  longestStreak: number;
  totalStudyDays: number;
  totalCompletedSessions: number;
  passRate: number;
  avgQuestionsPerSession: number;
  sessionModes: Array<{ mode: string; count: number }>;
  dailyActivity: Array<{ date: string; sessions: number; questions: number }>;
  accuracyTrend: Array<{ index: number; accuracy: number; date: string; mode: string }>;
  recentSessions: Array<{
    id: number; mode: string; category: string | null; track: string;
    questions: number; correct: number; accuracy: number; completedAt: string;
  }>;
  byCategory: Array<{ category: string; totalAnswered: number; totalCorrect: number; accuracy: number }>;
}


const MODE_COLORS: Record<string, string> = {
  practice: "bg-primary/80",
  exam: "bg-blue-500/80",
  weakness: "bg-red-500/80",
  category: "bg-amber-500/80",
};

function AccBar({ pct, size = "md" }: { pct: number; size?: "sm" | "md" }) {
  const color = pct >= 80 ? "#22c55e" : pct >= 60 ? "#eab308" : "#ef4444";
  const bg = pct >= 80 ? "bg-green-500" : pct >= 60 ? "bg-yellow-500" : "bg-red-500";
  const h = size === "sm" ? "h-1" : "h-1.5";
  return (
    <div className="flex items-center gap-2 w-full">
      <div className={cn("flex-1 bg-secondary rounded-full overflow-hidden", h)}>
        <div className={cn("h-full rounded-full", bg)} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs font-mono font-bold w-9 text-right" style={{ color }}>{pct.toFixed(0)}%</span>
    </div>
  );
}

function MetricNote({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-full p-0.5 text-muted-foreground/70 hover:text-foreground transition-colors"
          aria-label="Metric explanation"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 text-xs leading-relaxed font-mono cursor-pointer"
        onClick={() => setOpen(false)}
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

function StatCard({ icon: Icon, label, value, sub, note, highlight = false }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; note?: string; highlight?: boolean;
}) {
  return (
    <div className={cn(
      "bg-card border rounded-lg p-4 flex flex-col gap-1.5",
      highlight ? "border-primary/40 bg-primary/5" : "border-border"
    )}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={cn("w-3.5 h-3.5", highlight ? "text-primary" : "")} />
        <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
        {note ? <MetricNote text={note} /> : null}
      </div>
      <div className={cn("text-2xl font-bold font-mono", highlight ? "text-primary" : "text-foreground")}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground/60 font-mono">{sub}</div>}
    </div>
  );
}

function ActivityChart({ data }: { data: AnalyticsData["dailyActivity"] }) {
  const { t, language } = useLanguage();
  const a = t.analytics;
  const locale = language === "fr" ? "fr-CA" : "en-CA";
  const max = Math.max(...data.map(d => d.sessions), 1);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {data.map((d) => {
          const intensity = d.sessions === 0 ? 0 : Math.ceil((d.sessions / max) * 4);
          const bg = intensity === 0
            ? "bg-secondary/30"
            : intensity === 1 ? "bg-primary/20"
            : intensity === 2 ? "bg-primary/40"
            : intensity === 3 ? "bg-primary/65"
            : "bg-primary";
          const dt = new Date(d.date + "T12:00:00");
          const label = dt.toLocaleDateString(locale, { month: "short", day: "numeric" });
          return (
            <div
              key={d.date}
              title={`${label}: ${d.sessions} session${d.sessions !== 1 ? "s" : ""}, ${d.questions} questions`}
              className={cn("flex-1 rounded-sm aspect-square min-w-0 transition-all cursor-default", bg)}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground/40">
        <span>{new Date(data[0].date + "T12:00:00").toLocaleDateString(locale, { month: "short", day: "numeric" })}</span>
        <span>{a.activityToday}</span>
      </div>
      <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground/40">
        <span>{a.activityLess}</span>
        {["bg-secondary/30","bg-primary/20","bg-primary/40","bg-primary/65","bg-primary"].map((c, i) => (
          <div key={i} className={cn("w-3 h-3 rounded-sm", c)} />
        ))}
        <span>{a.activityMore}</span>
      </div>
    </div>
  );
}

function TrendChart({ data, modeLabels }: { data: AnalyticsData["accuracyTrend"]; modeLabels: Record<string, string> }) {
  const { t, language } = useLanguage();
  const locale = language === "fr" ? "fr-CA" : "en-CA";
  if (data.length === 0) return (
    <div className="text-center py-6 text-muted-foreground font-mono text-sm">{t.analytics.noSessionsChart}</div>
  );
  const max = 100;
  const avgAcc = data.reduce((a, d) => a + d.accuracy, 0) / data.length;

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-1 h-24">
        {data.map((d) => {
          const pct = (d.accuracy / max) * 100;
          const color = d.accuracy >= 80 ? "bg-green-500/70" : d.accuracy >= 60 ? "bg-yellow-500/70" : "bg-red-500/70";
          const dt = new Date(d.date + "T12:00:00");
          return (
            <div key={d.index} className="flex-1 flex flex-col items-center justify-end gap-0.5 group">
              <div
                className={cn("w-full rounded-t-sm transition-all group-hover:opacity-100 opacity-80", color)}
                style={{ height: `${Math.max(pct, 4)}%` }}
                title={`${modeLabels[d.mode] ?? d.mode} · ${dt.toLocaleDateString(locale, {month:"short",day:"numeric"})} · ${d.accuracy.toFixed(0)}%`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground/50">
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-green-500/70" /><span>≥80%</span></div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-yellow-500/70" /><span>60–80%</span></div>
        <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-red-500/70" /><span>&lt;60%</span></div>
        <span className="ml-auto">avg: <span className="text-foreground font-bold">{avgAcc.toFixed(0)}%</span></span>
      </div>
    </div>
  );
}

function GatedOverlay({ plan }: { plan: string }) {
  const { t } = useLanguage();
  const a = t.analytics;
  return (
    <div className="absolute inset-0 z-10 backdrop-blur-[3px] bg-background/60 rounded-lg flex flex-col items-center justify-center gap-3 p-6">
      <div className="bg-card border border-border rounded-lg p-5 text-center max-w-sm shadow-xl">
        <Lock className="w-6 h-6 text-primary mx-auto mb-2" />
        <p className="text-sm font-bold font-mono text-foreground mb-1">{a.lockedTitle}</p>
        <p className="text-xs text-muted-foreground mb-4">{a.lockedBody}</p>
        <Link href="/subscription" className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors">
          <Zap className="w-4 h-4" />
          {a.upgradeNow}
        </Link>
      </div>
    </div>
  );
}

/** Pro Plus analytics (“flight logbook”) — embedded on the flight deck or wrapped in `Layout` for legacy `/analytics`. */
export function FlightLogbookPanel({ variant = "page" }: { variant?: "page" | "section" } = {}) {
  const { plan } = useSubscription();
  const isProPlus = plan === "pro_plus";
  const { t, language } = useLanguage();
  const a = t.analytics;
  const locale = language === "fr" ? "fr-CA" : "en-CA";

  const { data, isLoading } = useQuery<AnalyticsData>({
    queryKey: ["stats-analytics"],
    queryFn: fetchAnalytics,
    staleTime: 300_000,
    enabled: isProPlus,
  });

  const totalModes = data?.sessionModes.reduce((a, m) => a + m.count, 0) ?? 1;
  const MODE_LABELS: Record<string, string> = {
    practice: a.modePractice,
    exam: a.modeExamSim,
    weakness: a.modeWeakness,
    category: a.modeCategory,
  };

  const Heading = variant === "section" ? "h2" : "h1";
  const titleCls =
    variant === "section"
      ? "text-2xl sm:text-3xl font-bold tracking-tight text-foreground"
      : "text-3xl font-bold tracking-tight text-foreground";

  return (
    <div
      id={variant === "section" ? "flight-logbook" : undefined}
      className={cn(
        "space-y-8 animate-in fade-in duration-500",
        variant === "section" && "scroll-mt-24 pt-8 mt-2 border-t border-border",
      )}
    >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Heading className={titleCls}>{a.title}</Heading>
            <p className="text-muted-foreground">{a.subtitle}</p>
          </div>
        </div>

        {/* Hero stat cards */}
        {isProPlus ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {isLoading ? (
              Array.from({length: 6}).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)
            ) : data ? (
              <>
                <StatCard icon={Flame} label={a.currentStreak} value={`${data.currentStreak}d`} sub={a.consecutiveDays} note={a.currentStreakNote} highlight />
                <StatCard icon={Trophy} label={a.longestStreak} value={`${data.longestStreak}d`} sub={a.personalBest} note={a.longestStreakNote} />
                <StatCard icon={CalendarDays} label={a.totalStudyDays} value={data.totalStudyDays} sub={a.totalDaysActive} note={a.totalStudyDaysNote} />
                <StatCard icon={Target} label={a.passRate} value={`${data.passRate.toFixed(0)}%`} sub="sessions ≥70%" note={a.passRateNote} />
                <StatCard icon={Crosshair} label={a.avgPerSession} value={data.avgQuestionsPerSession} sub={language === "fr" ? "questions" : "questions"} note={a.avgPerSessionNote} />
                <StatCard icon={CheckCircle2} label={a.sessionsCompleted} value={data.totalCompletedSessions} sub={a.sessionsDone} note={a.sessionsCompletedNote} />
              </>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { icon: Flame, label: a.lockedCurrentStreak, value: "—" },
              { icon: Trophy, label: a.lockedLongestStreak, value: "—" },
              { icon: CalendarDays, label: a.lockedStudyDays, value: "—" },
              { icon: Target, label: a.lockedPassRate, value: "—" },
              { icon: Crosshair, label: a.lockedAvgQ, value: "—" },
              { icon: CheckCircle2, label: a.lockedCompleted, value: "—" },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-card border border-border rounded-lg p-4 flex flex-col gap-1.5 opacity-50">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
                </div>
                <div className="text-2xl font-bold font-mono text-foreground">{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Gated analytics sections — single gate wraps all */}
        <div className="relative">
          {!isProPlus && <GatedOverlay plan={plan} />}
          <div className={cn("space-y-6", !isProPlus && "pointer-events-none select-none")}>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Activity Heatmap */}
              <div className="lg:col-span-2 bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarDays className="w-4 h-4 text-primary" />
                  <span className="text-sm font-bold font-mono tracking-wider text-foreground">{a.activityTitle}</span>
                </div>
                {isLoading ? <Skeleton className="h-16 w-full" /> : data ? (
                  <ActivityChart data={data.dailyActivity} />
                ) : (
                  <div className="h-16 flex items-center justify-center text-muted-foreground text-sm font-mono">{a.noActivityYet}</div>
                )}
              </div>

              {/* Session Modes */}
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <span className="text-sm font-bold font-mono tracking-wider text-foreground">{a.sessionModesTitle}</span>
                </div>
                {isLoading ? <Skeleton className="h-32 w-full" /> : data ? (
                  <div className="space-y-3">
                    {data.sessionModes.length === 0 ? (
                      <div className="text-sm text-muted-foreground font-mono text-center py-4">{a.noSessionsYet}</div>
                    ) : data.sessionModes.map(m => (
                      <div key={m.mode} className="space-y-1">
                        <div className="flex justify-between text-xs font-mono">
                          <span className="text-muted-foreground">{MODE_LABELS[m.mode] ?? m.mode}</span>
                          <span className="text-foreground font-bold">{m.count} <span className="text-muted-foreground/50">({((m.count/totalModes)*100).toFixed(0)}%)</span></span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={cn("h-full rounded-full", MODE_COLORS[m.mode] ?? "bg-primary/60")}
                            style={{ width: `${(m.count/totalModes)*100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Accuracy Trend */}
            <div className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold font-mono tracking-wider text-foreground">{a.accuracyTrendTitle}</span>
                <span className="text-[10px] text-muted-foreground/50 font-mono ml-1">({a.lastSessions})</span>
              </div>
              {isLoading ? <Skeleton className="h-24 w-full" /> : data ? (
                <TrendChart data={data.accuracyTrend} modeLabels={MODE_LABELS} />
              ) : null}
            </div>

            {/* Category Accuracy */}
            <div className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold font-mono tracking-wider text-foreground">{a.categoryAccTitle}</span>
              </div>
              {isLoading ? (
                <div className="space-y-3">{Array.from({length:5}).map((_,i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
              ) : data ? (
                data.byCategory.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground font-mono text-sm">{a.noAnswersYet}</div>
                ) : (
                  <div className="space-y-3">
                    {data.byCategory.map(c => (
                      <div key={c.category} className="space-y-1">
                        <div className="flex justify-between text-xs font-mono">
                          <span className="text-foreground">{c.category}</span>
                          <span className="text-muted-foreground/60">{c.totalAnswered} {a.answeredLabel}</span>
                        </div>
                        <AccBar pct={c.accuracy} />
                      </div>
                    ))}
                  </div>
                )
              ) : null}
            </div>

            {/* Recent Sessions */}
            <div className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold font-mono tracking-wider text-foreground">{a.recentSessionsTitle}</span>
              </div>
              {isLoading ? (
                <div className="space-y-2">{Array.from({length:5}).map((_,i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : data ? (
                data.recentSessions.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground font-mono text-sm">{a.noSessionsCompleted}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground/50 text-[10px] uppercase tracking-widest">
                          <th className="text-left pb-2 pr-4">{a.tableDate}</th>
                          <th className="text-left pb-2 pr-4">{a.tableMode}</th>
                          <th className="text-left pb-2 pr-4">{a.tableTrack}</th>
                          <th className="text-left pb-2 pr-4">{a.tableCategory}</th>
                          <th className="text-right pb-2 pr-4">{a.tableQuestions}</th>
                          <th className="text-right pb-2">{a.tableAccuracy}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recentSessions.map(s => {
                          const accColor = s.accuracy >= 80 ? "text-green-400" : s.accuracy >= 60 ? "text-yellow-400" : "text-red-400";
                          return (
                            <tr key={s.id} className="border-b border-border/30 hover:bg-secondary/20 transition-colors">
                              <td className="py-2 pr-4 text-muted-foreground">
                                {new Date(s.completedAt).toLocaleDateString(locale, { month:"short", day:"numeric" })}
                              </td>
                              <td className="py-2 pr-4 text-foreground">{MODE_LABELS[s.mode] ?? s.mode}</td>
                              <td className="py-2 pr-4 text-muted-foreground uppercase">{s.track}</td>
                              <td className="py-2 pr-4 text-muted-foreground max-w-[120px] truncate">{s.category ?? "—"}</td>
                              <td className="py-2 pr-4 text-right text-foreground">{s.questions}</td>
                              <td className={cn("py-2 text-right font-bold", accColor)}>{s.accuracy.toFixed(0)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              ) : null}
            </div>

          </div>
        </div>

        {/* Non-pro upgrade CTA at bottom */}
        {!isProPlus && (
          <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-primary/30 bg-primary/5">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">{a.unlockAnalytics}</p>
                <p className="text-xs text-muted-foreground">{a.unlockAnalyticsDesc}</p>
              </div>
            </div>
            <Link href="/subscription" className="inline-flex shrink-0 items-center gap-2 justify-center whitespace-nowrap rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 transition-colors">
              <Zap className="w-4 h-4" />
              {a.upgradeBtn}
            </Link>
          </div>
        )}
    </div>
  );
}

export default function AnalyticsPage() {
  return (
    <Layout>
      <FlightLogbookPanel />
    </Layout>
  );
}
